import { format } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/modules/shared/ui/components/card'
import { Badge } from '@/modules/shared/ui/components/badge'
import { Button } from '@/modules/shared/ui/components/button'
import { Loader2, Check, X, BarChart3 } from 'lucide-react'
import type { ScoreBreakdownDto } from '@/modules/recruitment/applications/dtos/ApplicationDetails'
import type { VacancySuggestionDto } from '../../dtos/vacancy-suggestion.dto'

interface ScoreColumnProps {
  label: string
  vacancyTitle: string
  score: ScoreBreakdownDto | null
  scoringStatus?: string
  scoredAt?: string | null
}

function ScoreColumn({ label, vacancyTitle, score, scoringStatus, scoredAt }: ScoreColumnProps) {
  const categoryOrder = ['RequiredSkills', 'OptionalSkills', 'KeyResponsibilities', 'Certification']
  const categoryConfig: Record<string, { label: string; badgeClass: string }> = {
    RequiredSkills: { label: 'Required Skills', badgeClass: 'bg-red-50 text-red-700 border-red-200' },
    OptionalSkills: { label: 'Optional Skills', badgeClass: 'bg-primary/5 text-primary border-primary/20' },
    KeyResponsibilities: { label: 'Key Responsibilities', badgeClass: 'bg-amber-50 text-amber-700 border-amber-200' },
    Certification: { label: 'Certifications', badgeClass: 'bg-purple-50 text-purple-700 border-purple-200' },
  }

  const grouped = (score?.skillBreakdown ?? []).reduce<Record<string, ScoreBreakdownDto['skillBreakdown']>>(
    (acc, skill) => {
      const cat = skill.category || 'Other'
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(skill)
      return acc
    },
    {},
  )

  const sortedCategories = [
    ...categoryOrder.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !categoryOrder.includes(c)),
  ]

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="font-semibold text-foreground truncate" title={vacancyTitle}>
            {vacancyTitle}
          </p>
        </div>
        {score && (
          <Badge className="bg-primary/20 text-primary border-primary/30 text-base px-3 py-0.5 shrink-0">
            {score.score}%
          </Badge>
        )}
        {!score && scoringStatus === 'Pending' && (
          <Badge variant="outline" className="text-muted-foreground">Pending</Badge>
        )}
        {!score && scoringStatus === 'Failed' && (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Failed</Badge>
        )}
      </div>

      {score && (
        <>
          {scoredAt && (
            <p className="text-[11px] text-muted-foreground mb-3">
              Scored {format(new Date(scoredAt), "MMM d, yyyy 'at' h:mm a")}
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <Card className="border-green-200">
              <CardHeader className="bg-green-50/50 border-b border-green-100 pb-2 px-3 pt-3">
                <CardTitle className="text-xs font-semibold text-green-800 flex items-center gap-1">
                  <Check className="w-3 h-3 text-green-600" />
                  Strengths
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2 px-3 pb-3">
                {(score.strengths ?? []).length > 0 ? (
                  <ul className="space-y-1">
                    {score.strengths.map((s, i) => (
                      <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                        <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground italic">None identified</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-red-200">
              <CardHeader className="bg-red-50/50 border-b border-red-100 pb-2 px-3 pt-3">
                <CardTitle className="text-xs font-semibold text-red-800 flex items-center gap-1">
                  <X className="w-3 h-3 text-red-500" />
                  Weaknesses
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2 px-3 pb-3">
                {(score.weaknesses ?? []).length > 0 ? (
                  <ul className="space-y-1">
                    {score.weaknesses.map((w, i) => (
                      <li key={i} className="text-xs text-foreground flex items-start gap-1.5">
                        <X className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground italic">None identified</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-3">
            {sortedCategories.map((category) => {
              const config = categoryConfig[category] ?? { label: category, badgeClass: 'bg-muted text-foreground border-border' }
              const skills = grouped[category]
              return (
                <div key={category} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-2 py-0.5 font-semibold border ${config.badgeClass}`}
                    >
                      {config.label}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {skills.length} item{skills.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid gap-1.5">
                    {skills.map((skill, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 rounded-md border border-border bg-muted/30 px-3 py-2"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground">{skill.skillName}</p>
                          <p className="text-[10px] text-muted-foreground">{skill.explanation}</p>
                        </div>
                        <div className="shrink-0 w-24">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] text-muted-foreground">Match</span>
                            <span className="text-[10px] font-semibold">{skill.matchPercentage}%</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                skill.matchPercentage >= 80
                                  ? 'bg-green-500'
                                  : skill.matchPercentage >= 60
                                    ? 'bg-yellow-500'
                                    : 'bg-red-400'
                              }`}
                              style={{ width: `${skill.matchPercentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

interface Props {
  suggestion: VacancySuggestionDto
  vacancyATitle: string
  vacancyAScore: ScoreBreakdownDto | null
  onSuggest: () => void
  onKeep: () => void
  isSuggestDisabled?: boolean
}

export function VacancyComparisonPanel({
  suggestion,
  vacancyATitle,
  vacancyAScore,
  onSuggest,
  onKeep,
  isSuggestDisabled = false,
}: Props) {
  const canSuggest =
    suggestion.status === 'Compared' &&
    suggestion.comparisonScoringStatus === 'Scored' &&
    !isSuggestDisabled

  return (
    <Card className="bg-card shadow-md border-border">
      <CardHeader className="bg-primary/5 border-b border-border">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-foreground flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Vacancy Comparison
          </CardTitle>
          {suggestion.status === 'Compared' && suggestion.comparisonScoringStatus === 'Pending' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Scoring in progress…
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <ScoreColumn
            label="Vacancy A (current)"
            vacancyTitle={vacancyATitle}
            score={vacancyAScore}
            scoredAt={vacancyAScore?.scoredAt}
          />

          <div className="hidden lg:flex items-stretch">
            <div className="w-px bg-border" />
          </div>
          <div className="lg:hidden border-t border-border" />

          <ScoreColumn
            label="Vacancy B (target)"
            vacancyTitle={suggestion.targetVacancyTitle}
            score={suggestion.comparisonScore}
            scoringStatus={suggestion.comparisonScoringStatus}
            scoredAt={suggestion.comparisonScoredAt}
          />
        </div>

        {suggestion.status !== 'Suggested' &&
          suggestion.status !== 'Accepted' &&
          suggestion.status !== 'Rejected' && (
            <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-border">
              <Button variant="outline" onClick={onKeep} className="flex-1">
                Stay on current
              </Button>
              <Button
                onClick={onSuggest}
                disabled={!canSuggest}
                className="flex-1"
              >
                Suggest for new
              </Button>
            </div>
          )}

        {suggestion.status === 'Suggested' && (
          <div className="mt-6 pt-6 border-t border-border rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
            <p className="text-sm font-medium text-blue-800">
              Candidate has been suggested for{' '}
              <span className="font-semibold">{suggestion.targetVacancyTitle}</span>.
            </p>
            {suggestion.tokenExpiresAt && (
              <p className="text-xs text-blue-600 mt-0.5">
                Link expires {format(new Date(suggestion.tokenExpiresAt), "MMM d, yyyy")}
              </p>
            )}
          </div>
        )}

        {suggestion.status === 'Accepted' && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3">
              <p className="text-sm font-medium text-green-800">
                Candidate accepted and is now in the <span className="font-semibold">{suggestion.targetVacancyTitle}</span> pipeline.
              </p>
            </div>
          </div>
        )}

        {suggestion.status === 'Rejected' && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="rounded-lg bg-muted border border-border px-4 py-3">
              <p className="text-sm font-medium text-foreground">
                Candidate declined the suggestion for{' '}
                <span className="font-semibold">{suggestion.targetVacancyTitle}</span>.
              </p>
              {suggestion.candidateResponseNote && (
                <p className="text-xs text-muted-foreground mt-1">
                  Note: {suggestion.candidateResponseNote}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
